/**
 * Matapayals AI Assistant ("Payal")
 * A self-contained, dependency-free FAQ chat assistant with smart keyword
 * intent-matching and a WhatsApp human-handoff. Designed to feel like a live
 * AI chat while running fully client-side (no API keys, no backend cost).
 *
 * Upgrade path: to plug a real LLM, replace `resolveAnswer()` with a fetch()
 * to your endpoint and stream the reply into addBotMessage(). The knowledge
 * base below can be sent as system context.
 */
(function () {
  'use strict';

  var CFG = window.MP_CHAT_CONFIG || {};
  var WA = (CFG.wa || '919902685925').replace(/[^0-9]/g, '');
  var EMAIL = CFG.email || 'support@matapayals.com';
  var PHONE = CFG.phone || '08023562905';
  var ACCOUNT_URL = CFG.accountUrl || '/account';
  var ASSISTANT = CFG.assistant || 'Payal';
  var STORAGE_KEY = 'mp_chat_history_v1';

  function waLink(text) {
    return 'https://wa.me/' + WA + '?text=' + encodeURIComponent(text || 'Hi Matapayals, I have a question.');
  }

  /* ---------------------------------------------------------------------------
   * Knowledge base — each intent has trigger keywords, a quick-reply chip label,
   * and an HTML answer. Answers use the store's real info & policy links.
   * ------------------------------------------------------------------------- */
  var KB = [
    {
      id: 'delivery_time',
      chip: '🚚 Delivery time',
      kw: ['delivery', 'deliver', 'how long', 'days', 'arrive', 'dispatch', 'ship time', 'shipping time', 'when will', 'reach'],
      a: 'We usually dispatch orders within <b>2–4 business days</b>. Delivery across India then takes about <b>4–7 business days</b>, depending on your location. You\'ll get a tracking link by SMS/email once it ships. Want an exact estimate for your pincode? Tap <b>Chat with a human</b> below.'
    },
    {
      id: 'shipping_cost',
      chip: '📦 Shipping charges',
      kw: ['shipping charge', 'shipping cost', 'delivery charge', 'delivery cost', 'free shipping', 'courier charge', 'postage'],
      a: 'Shipping is calculated at checkout based on your order value and location, and we often run <b>free shipping</b> on prepaid orders. Full details are on our <a href="/policies/shipping-policy" target="_blank" rel="noopener">Shipping Policy</a> page.'
    },
    {
      id: 'international',
      chip: '🌍 International shipping',
      kw: ['international', 'abroad', 'outside india', 'overseas', 'usa', 'uk', 'canada', 'australia', 'worldwide', 'other country'],
      a: 'Yes — we ship <b>worldwide</b>! 🌍 International charges and timelines are shown at checkout. See our <a href="/policies/shipping-policy" target="_blank" rel="noopener">Shipping Policy</a>, or ask a human for help with a specific country.'
    },
    {
      id: 'track',
      chip: '📍 Track my order',
      kw: ['track', 'tracking', 'where is my order', 'order status', 'awb', 'shipped yet', 'my package', 'my parcel'],
      a: 'To track your order:<br>1. Open <a href="' + ACCOUNT_URL + '" target="_blank" rel="noopener">My Account → Orders</a><br>2. Or use the tracking link in your shipping SMS/email.<br>Still need help? Share your order number with our team and we\'ll check it for you.',
      actions: [{ label: 'Check with a human', wa: 'Hi Matapayals, I\'d like to track my order. My order number is: ' }]
    },
    {
      id: 'returns',
      chip: '🔁 Returns & exchange',
      kw: ['return', 'exchange', 'refund', 'replace', 'replacement', 'damaged', 'broken', 'wrong item', 'defective', 'cancel'],
      a: 'We offer easy <b>exchanges</b> as per our policy. If an item arrives damaged or incorrect, contact us within the eligible window with an <b>unboxing photo/video</b> and we\'ll make it right. Full details: <a href="/policies/refund-policy" target="_blank" rel="noopener">Exchange &amp; Refund Policy</a>.'
    },
    {
      id: 'material',
      chip: '💎 Material & purity',
      kw: ['material', 'silver', '925', 'sterling', 'metal', 'purity', 'gold', 'made of', 'real silver', 'genuine', 'hallmark', 'quality'],
      a: 'Most of our pieces are crafted in <b>hallmarked 925 sterling silver</b> 💎. The exact metal, purity and weight are listed on each product page. Need details on a specific design? Tap <b>Chat with a human</b>.'
    },
    {
      id: 'care',
      chip: '✨ Silver care',
      kw: ['care', 'clean', 'maintain', 'tarnish', 'black', 'blacken', 'polish', 'store', 'shine', 'dull', 'oxidise', 'oxidize'],
      a: 'To keep your silver shining ✨<br>• Store it dry in an airtight pouch<br>• Keep away from perfume, water &amp; sweat<br>• Wipe with a soft cloth after use<br>Read our full <a href="/pages/silver-care" target="_blank" rel="noopener">Silver Care guide</a>.'
    },
    {
      id: 'payment',
      chip: '💳 Payment & COD',
      kw: ['payment', 'cod', 'cash on delivery', 'upi', 'card', 'emi', 'pay', 'razorpay', 'netbanking', 'net banking', 'wallet', 'gpay', 'paytm'],
      a: 'We accept <b>UPI, credit/debit cards, net banking, wallets and EMI</b> via secure Razorpay checkout. 💳 COD availability can vary by pincode — confirm yours with a human on WhatsApp.'
    },
    {
      id: 'size',
      chip: '📏 Sizing help',
      kw: ['size', 'sizing', 'fit', 'adjustable', 'measure', 'measurement', 'bangle size', 'ring size', 'anklet length', 'length', 'too small', 'too big'],
      a: 'Many of our anklets, rings and bangles are <b>adjustable</b> 📏. Each product page lists size/length details. Not sure about a fit? Send us the design and your measurement and we\'ll guide you personally.',
      actions: [{ label: 'Get sizing help', wa: 'Hi Matapayals, I need sizing help for: ' }]
    },
    {
      id: 'store',
      chip: '🏬 Store & hours',
      kw: ['store', 'shop address', 'location', 'address', 'visit', 'offline', 'bengaluru', 'bangalore', 'malleshwaram', 'timing', 'hours', 'open', 'showroom'],
      a: 'Visit our store 🏬<br><b>Matapayals</b><br>441, Cellar Floor, Venkata Ranga Iyengar Rd, near City Central Library, Malleshwaram, Bengaluru 560003<br>🕚 Open <b>11:00 AM – 8:30 PM</b><br>☎ <a href="tel:' + PHONE + '">' + PHONE + '</a>'
    },
    {
      id: 'offers',
      chip: '🎁 Offers & discounts',
      kw: ['offer', 'discount', 'coupon', 'code', 'sale', 'deal', 'promo', 'cheap', 'price drop'],
      a: 'Check our home page for current offers 🎁. New drops and festive deals go out on our Instagram and email first. Looking for a code right now? Ask a human below and we\'ll help.'
    },
    {
      id: 'custom',
      chip: '🎀 Custom / bulk / gifting',
      kw: ['custom', 'customise', 'customize', 'bulk', 'wholesale', 'engraving', 'gift', 'gifting', 'personalise', 'personalize', 'name'],
      a: 'We\'d love to help with custom, gifting or bulk orders! 🎀 Share your idea with our team and we\'ll assist you personally.',
      actions: [{ label: 'Discuss my idea', wa: 'Hi Matapayals, I\'m interested in a custom/bulk order: ' }]
    }
  ];

  // Which chips to show as the opening quick-replies (8, like the reference).
  var OPENING_CHIPS = ['delivery_time', 'track', 'returns', 'material', 'care', 'payment', 'store', 'custom'];

  var GREETING = 'Hi! 👋 I\'m <b>' + ASSISTANT + '</b>, your personal jewellery assistant at Matapayals. Ask me anything — delivery, silver care, sizing, returns and more. How can I help you today? 💎';

  var FALLBACK = 'I\'m not fully sure about that one 🤔 — but our team can help you right away. Tap <b>Chat with a human</b> to reach us on WhatsApp, or pick one of the quick questions below.';

  /* --------------------------- intent matching --------------------------- */
  function normalize(s) { return (' ' + s.toLowerCase() + ' ').replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' '); }

  function resolveAnswer(text) {
    var q = normalize(text);
    var best = null, bestScore = 0;
    for (var i = 0; i < KB.length; i++) {
      var intent = KB[i], score = 0;
      for (var k = 0; k < intent.kw.length; k++) {
        var kw = intent.kw[k];
        if (q.indexOf(' ' + kw + ' ') !== -1 || q.indexOf(' ' + kw) !== -1) {
          score += kw.indexOf(' ') !== -1 ? 3 : 2; // phrases weigh more
        }
      }
      if (score > bestScore) { bestScore = score; best = intent; }
    }
    return bestScore >= 2 ? best : null;
  }

  /* ------------------------------- DOM ---------------------------------- */
  var root, panel, body, launcher, teaser, inputEl, sendBtn, openState = false, greeted = false;

  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  function scrollBottom() { body.scrollTop = body.scrollHeight; }

  function saveHistory() {
    try {
      var msgs = [].slice.call(body.querySelectorAll('.mpc-msg')).map(function (m) {
        return { who: m.classList.contains('mpc-msg--user') ? 'user' : 'bot', html: m.querySelector('.mpc-bubble').innerHTML };
      });
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(msgs.slice(-30)));
    } catch (e) {}
  }

  function addMessage(html, who) {
    var wrap = el('div', 'mpc-msg mpc-msg--' + who);
    if (who === 'bot') wrap.appendChild(el('div', 'mpc-ava', avatarSVG()));
    wrap.appendChild(el('div', 'mpc-bubble', html));
    body.appendChild(wrap);
    scrollBottom();
    saveHistory();
    return wrap;
  }

  function addUserMessage(text) { addMessage(escapeHtml(text), 'user'); }

  function typingIndicator() {
    var wrap = el('div', 'mpc-msg mpc-msg--bot mpc-typing');
    wrap.appendChild(el('div', 'mpc-ava', avatarSVG()));
    wrap.appendChild(el('div', 'mpc-bubble', '<span class="mpc-dot"></span><span class="mpc-dot"></span><span class="mpc-dot"></span>'));
    body.appendChild(wrap);
    scrollBottom();
    return wrap;
  }

  function addBotMessage(html, actions) {
    var typing = typingIndicator();
    var delay = Math.min(900, 350 + html.length * 4);
    setTimeout(function () {
      typing.parentNode && typing.parentNode.removeChild(typing);
      var msg = addMessage(html, 'bot');
      if (actions && actions.length) {
        var row = el('div', 'mpc-actions');
        actions.forEach(function (act) {
          var a = el('a', 'mpc-action-btn', act.label);
          a.href = act.href || waLink(act.wa);
          a.target = '_blank'; a.rel = 'noopener';
          row.appendChild(a);
        });
        msg.querySelector('.mpc-bubble').appendChild(row);
      }
      scrollBottom();
      saveHistory();
    }, delay);
  }

  function renderChips(ids) {
    var wrap = el('div', 'mpc-chips');
    ids.forEach(function (id) {
      var intent = KB.filter(function (x) { return x.id === id; })[0];
      if (!intent) return;
      var chip = el('button', 'mpc-chip', intent.chip);
      chip.type = 'button';
      chip.addEventListener('click', function () { handleIntent(intent, intent.chip); });
      wrap.appendChild(chip);
    });
    // Always offer a human
    var human = el('button', 'mpc-chip mpc-chip--human', '💬 Chat with a human');
    human.type = 'button';
    human.addEventListener('click', humanHandoff);
    wrap.appendChild(human);
    body.appendChild(wrap);
    scrollBottom();
  }

  function handleIntent(intent, label) {
    addUserMessage(label.replace(/^[^\w]+/, '').trim());
    addBotMessage(intent.a, intent.actions);
  }

  function humanHandoff() {
    addUserMessage('Chat with a human');
    addBotMessage(
      'Sure! Our team is here to help. 💬<br>Reach us instantly on WhatsApp, or email <a href="mailto:' + EMAIL + '">' + EMAIL + '</a>.<br>🕚 We reply during store hours, 11:00 AM – 8:30 PM.',
      [{ label: '💬 Open WhatsApp', wa: 'Hi Matapayals, I\'d like to talk to a team member.' }]
    );
  }

  function handleUserText(text) {
    text = (text || '').trim();
    if (!text) return;
    addUserMessage(text);
    var intent = resolveAnswer(text);
    if (intent) {
      addBotMessage(intent.a, intent.actions);
    } else {
      addBotMessage(FALLBACK, [{ label: '💬 Chat with a human', wa: 'Hi Matapayals, I have a question: ' + text }]);
      setTimeout(function () { renderChips(OPENING_CHIPS.slice(0, 4)); }, 1000);
    }
  }

  /* ------------------------------- boot --------------------------------- */
  function greet() {
    if (greeted) return;
    greeted = true;
    var restored = false;
    try {
      var saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
      if (saved.length) {
        saved.forEach(function (m) { addMessage(m.html, m.who); });
        restored = true;
      }
    } catch (e) {}
    if (!restored) {
      addBotMessage(GREETING);
      setTimeout(function () { renderChips(OPENING_CHIPS); }, 950);
    }
  }

  function openPanel() {
    openState = true;
    root.classList.add('mpc-open');
    document.body.classList.add('mpc-lock');
    hideTeaser();
    greet();
    setTimeout(function () { inputEl && inputEl.focus(); }, 300);
  }
  function closePanel() {
    openState = false;
    root.classList.remove('mpc-open');
    document.body.classList.remove('mpc-lock');
    launcher.focus();
  }
  function toggle() { openState ? closePanel() : openPanel(); }

  function hideTeaser() { if (teaser) teaser.classList.add('mpc-hide'); }

  function build() {
    root = el('div', 'mpc-root');
    root.setAttribute('data-mpc', '');

    // Launcher
    launcher = el('button', 'mpc-launcher', sparkleSVG() + '<span class="mpc-launcher-pulse"></span>');
    launcher.type = 'button';
    launcher.setAttribute('aria-label', 'Chat with ' + ASSISTANT + ', the Matapayals assistant');
    launcher.addEventListener('click', toggle);

    // Teaser bubble
    teaser = el('div', 'mpc-teaser',
      '<button class="mpc-teaser-x" aria-label="Dismiss">&times;</button>' +
      '<div class="mpc-teaser-ava">' + avatarSVG() + '</div>' +
      '<div class="mpc-teaser-txt">Jewellery assistant<br><span>Hi! 👋</span></div>');
    teaser.querySelector('.mpc-teaser-x').addEventListener('click', function (e) { e.stopPropagation(); hideTeaser(); });
    teaser.addEventListener('click', openPanel);

    // Panel
    panel = el('div', 'mpc-panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Matapayals chat assistant');

    var header = el('div', 'mpc-header',
      '<div class="mpc-header-ava">' + avatarSVG() + '<span class="mpc-online"></span></div>' +
      '<div class="mpc-header-txt"><div class="mpc-header-name">' + ASSISTANT + ' · Matapayals</div>' +
      '<div class="mpc-header-status">Instant answers, 24/7 💎</div></div>');
    var close = el('button', 'mpc-close', '&times;');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close chat');
    close.addEventListener('click', closePanel);
    header.appendChild(close);

    body = el('div', 'mpc-body');

    var footer = el('form', 'mpc-input');
    inputEl = el('input');
    inputEl.type = 'text';
    inputEl.placeholder = 'Type your question…';
    inputEl.setAttribute('aria-label', 'Type your question');
    sendBtn = el('button', 'mpc-send', sendSVG());
    sendBtn.type = 'submit';
    sendBtn.setAttribute('aria-label', 'Send');
    footer.appendChild(inputEl);
    footer.appendChild(sendBtn);
    footer.addEventListener('submit', function (e) {
      e.preventDefault();
      var v = inputEl.value;
      inputEl.value = '';
      handleUserText(v);
    });

    var brand = el('div', 'mpc-foot', 'Powered by Matapayals ✦ <a href="' + waLink('Hi Matapayals!') + '" target="_blank" rel="noopener">WhatsApp us</a>');

    panel.appendChild(header);
    panel.appendChild(body);
    panel.appendChild(footer);
    panel.appendChild(brand);

    root.appendChild(panel);
    root.appendChild(teaser);
    root.appendChild(launcher);
    document.body.appendChild(root);

    // Auto-show teaser after a beat (once per session)
    if (!sessionStorage.getItem('mp_chat_teaser_seen')) {
      setTimeout(function () {
        if (!openState) { teaser.classList.add('mpc-show'); sessionStorage.setItem('mp_chat_teaser_seen', '1'); }
      }, 2600);
    }

    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && openState) closePanel(); });
  }

  /* ------------------------------ icons --------------------------------- */
  function sparkleSVG() {
    return '<svg class="mpc-i" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<path d="M12 2l1.6 4.8a3 3 0 0 0 1.9 1.9L20 10.3l-4.5 1.6a3 3 0 0 0-1.9 1.9L12 18.6l-1.6-4.8a3 3 0 0 0-1.9-1.9L4 10.3l4.5-1.6a3 3 0 0 0 1.9-1.9L12 2z" fill="currentColor"/>' +
      '<circle cx="18.5" cy="17.5" r="1.6" fill="currentColor"/><circle cx="5.5" cy="16.5" r="1.1" fill="currentColor"/></svg>';
  }
  function avatarSVG() {
    return '<svg viewBox="0 0 40 40" aria-hidden="true" class="mpc-ava-svg">' +
      '<defs><linearGradient id="mpcg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#c9a34e"/><stop offset="1" stop-color="#ae2537"/></linearGradient></defs>' +
      '<circle cx="20" cy="20" r="20" fill="url(#mpcg)"/>' +
      '<path d="M20 9l2.4 5.1 5.6.6-4.2 3.8 1.2 5.5L20 26.4 14.9 24l1.2-5.5L12 14.7l5.6-.6L20 9z" fill="#fff" opacity=".95"/></svg>';
  }
  function sendSVG() { return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.4 20.4l17.5-8a1 1 0 0 0 0-1.8l-17.5-8A1 1 0 0 0 2 3.5l1.9 6.8 9.1 1.7-9.1 1.7L2 20.5a1 1 0 0 0 1.4 0z" fill="currentColor"/></svg>'; }
  function escapeHtml(s) { return s.replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build);
  } else {
    build();
  }
})();
